const bindWebsitePagePonoAiEditor = () => {
    const root = document.getElementById('admin2026-pono-ai-page-editor');
    if (!root || root.dataset.bound === 'true')
    {
        return Boolean(root);
    }

    const form = root.querySelector('[data-pono-ai-editor-form]');
    const prompt = root.querySelector('[data-pono-ai-editor-prompt]');
    const characterCount = root.querySelector('[data-pono-ai-editor-character-count]');
    const dictationButton = root.querySelector('[data-pono-ai-editor-dictation]');
    const dictationIcon = root.querySelector('[data-pono-ai-editor-dictation-icon]');
    const dictationStatus = root.querySelector('[data-pono-ai-editor-dictation-status]');
    const generateButton = root.querySelector('[data-pono-ai-editor-generate]');
    const generateLabel = root.querySelector('[data-pono-ai-editor-generate-label]');
    const status = root.querySelector('[data-pono-ai-editor-status]');
    const result = root.querySelector('[data-pono-ai-editor-result]');
    const resultLabel = root.querySelector('[data-pono-ai-editor-result-label]');
    const summary = root.querySelector('[data-pono-ai-editor-summary]');
    const changes = root.querySelector('[data-pono-ai-editor-changes]');
    const preview = root.querySelector('[data-pono-ai-editor-preview]');
    const applyButton = root.querySelector('[data-pono-ai-editor-apply]');
    const discardButton = root.querySelector('[data-pono-ai-editor-discard]');

    if (!form || !prompt || !characterCount || !generateButton || !generateLabel || !status || !result || !resultLabel || !summary || !changes || !preview || !applyButton || !discardButton)
    {
        return false;
    }

    root.dataset.bound = 'true';
    const state = {
        draftUuid: null,
        revisionSha256: null,
        livePageSha256: null,
        busy: false,
        recognition: null,
        dictating: false,
        dictationStopping: false,
        dictationBase: '',
        dictationStopTimer: null,
        dictationHadError: false,
    };

    const dictationConstructor = () => {
        if (typeof window === 'undefined')
        {
            return null;
        }

        return window.SpeechRecognition || window.webkitSpeechRecognition || null;
    };

    const dictationSupported = () => {
        return Boolean(
            dictationButton
            && dictationIcon
            && dictationStatus
            && typeof dictationConstructor() === 'function'
        );
    };

    const setDictationStatus = (message, isError = false) => {
        if (!dictationStatus)
        {
            return;
        }

        dictationStatus.textContent = message || '';
        dictationStatus.hidden = !message;
        dictationStatus.classList.toggle('text-rose-600', Boolean(message) && isError);
        dictationStatus.classList.toggle('dark:text-rose-300', Boolean(message) && isError);
        dictationStatus.classList.toggle('text-blue-600', Boolean(message) && !isError);
        dictationStatus.classList.toggle('dark:text-blue-300', Boolean(message) && !isError);
    };

    const setPromptDictationActive = (active) => {
        prompt.readOnly = active;

        if (active)
        {
            prompt.setAttribute('aria-busy', 'true');
        }
        else
        {
            prompt.removeAttribute('aria-busy');
        }
    };

    const updateDictationButton = () => {
        if (!dictationButton || !dictationIcon)
        {
            return;
        }

        const supported = dictationSupported();
        const label = state.dictationStopping
            ? 'Stopping dictation'
            : (state.dictating ? 'Stop dictation' : 'Start dictation');

        dictationButton.hidden = !supported;
        dictationButton.disabled = !supported || state.busy || state.dictationStopping;
        dictationButton.setAttribute('aria-label', label);
        dictationButton.setAttribute('aria-pressed', state.dictating ? 'true' : 'false');
        dictationButton.setAttribute('title', label);
        dictationIcon.className = state.dictationStopping
            ? 'fa-solid fa-spinner fa-spin'
            : (state.dictating ? 'fa-solid fa-stop' : 'fa-solid fa-microphone');
    };

    const clearDictationStopTimer = () => {
        if (state.dictationStopTimer && typeof window !== 'undefined')
        {
            window.clearTimeout(state.dictationStopTimer);
            state.dictationStopTimer = null;
        }
    };

    const releaseDictation = (recognition) => {
        if (state.recognition !== recognition)
        {
            return false;
        }

        clearDictationStopTimer();
        state.recognition = null;
        state.dictating = false;
        state.dictationStopping = false;
        state.dictationBase = '';
        state.dictationHadError = false;
        setPromptDictationActive(false);
        updateDictationButton();

        return true;
    };

    const dictationErrorMessage = (code) => {
        switch (String(code || '').trim().toLowerCase())
        {
            case 'not-allowed':
            case 'service-not-allowed':
                return 'Microphone access was blocked. Allow microphone access and try again.';
            case 'audio-capture':
                return 'No microphone was detected. Connect a microphone and try again.';
            case 'no-speech':
                return 'No speech was detected. Try again when you are ready.';
            case 'network':
                return 'Dictation could not connect. Check your connection and try again.';
            default:
                return 'Dictation could not start. You can continue typing your request.';
        }
    };

    const dictatedPromptValue = (base, transcript) => {
        const initial = String(base || '').trim();
        const dictated = String(transcript || '').replace(/\s+/g, ' ').trim();
        const combined = initial && dictated ? `${initial} ${dictated}` : (initial || dictated);
        const maxLength = parseInt(prompt.maxLength || '0', 10);

        return maxLength > 0 ? combined.slice(0, maxLength) : combined;
    };

    function stopDictation(abort = false)
    {
        const recognition = state.recognition;

        if (!recognition)
        {
            state.dictating = false;
            state.dictationStopping = false;
            state.dictationBase = '';
            state.dictationHadError = false;
            setPromptDictationActive(false);

            if (abort)
            {
                setDictationStatus('');
            }

            updateDictationButton();
            return false;
        }

        try
        {
            if (abort && typeof recognition.abort === 'function')
            {
                clearDictationStopTimer();
                state.recognition = null;
                recognition.onstart = null;
                recognition.onresult = null;
                recognition.onerror = null;
                recognition.onend = null;
                state.dictating = false;
                state.dictationStopping = false;
                state.dictationBase = '';
                state.dictationHadError = false;
                setPromptDictationActive(false);
                setDictationStatus('');
                updateDictationButton();
                recognition.abort();
                return true;
            }

            if (state.dictationStopping)
            {
                return true;
            }

            if (typeof recognition.stop !== 'function')
            {
                releaseDictation(recognition);
                return false;
            }

            state.dictationStopping = true;
            updateDictationButton();
            recognition.stop();

            if (state.recognition === recognition && typeof window !== 'undefined')
            {
                state.dictationStopTimer = window.setTimeout(() => {
                    if (state.recognition !== recognition)
                    {
                        return;
                    }

                    const hadError = state.dictationHadError;
                    recognition.onstart = null;
                    recognition.onresult = null;
                    recognition.onerror = null;
                    recognition.onend = null;
                    const released = releaseDictation(recognition);

                    if (typeof recognition.abort === 'function')
                    {
                        try
                        {
                            recognition.abort();
                        }
                        catch (error)
                        {
                            // The browser already released the speech session.
                        }
                    }

                    if (released && !hadError)
                    {
                        setDictationStatus('Dictation added. Review your request or continue typing.');
                        prompt.focus();
                    }
                }, 2000);
            }

            return true;
        }
        catch (error)
        {
            releaseDictation(recognition);
            if (!abort)
            {
                setDictationStatus('Dictation stopped. Review your request or continue typing.', true);
            }
            return false;
        }
    }

    const startDictation = () => {
        const Recognition = dictationConstructor();

        if (typeof Recognition !== 'function' || !dictationSupported())
        {
            setDictationStatus('Dictation is not available in this browser. You can continue typing your request.', true);
            return false;
        }

        stopDictation(true);

        let recognition = null;

        try
        {
            recognition = new Recognition();
        }
        catch (error)
        {
            setDictationStatus(dictationErrorMessage(error && error.name), true);
            return false;
        }

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = (typeof navigator !== 'undefined' && navigator.language)
            ? navigator.language
            : ((document.documentElement && document.documentElement.lang) ? document.documentElement.lang : 'en-US');

        state.recognition = recognition;
        state.dictating = true;
        state.dictationStopping = false;
        state.dictationBase = String(prompt.value || '').trim();
        state.dictationHadError = false;
        setPromptDictationActive(true);
        setDictationStatus('Listening… click the microphone to stop.');
        updateDictationButton();

        recognition.onstart = () => {
            if (state.recognition === recognition)
            {
                state.dictating = true;
                setDictationStatus('Listening… click the microphone to stop.');
                updateDictationButton();
            }
        };

        recognition.onresult = (event) => {
            if (state.recognition !== recognition)
            {
                return;
            }

            const parts = [];
            const results = event && event.results ? event.results : [];

            for (let index = 0; index < results.length; index++)
            {
                const alternative = results[index] && results[index][0] ? results[index][0] : null;
                const value = alternative ? String(alternative.transcript || '').trim() : '';

                if (value)
                {
                    parts.push(value);
                }
            }

            prompt.value = dictatedPromptValue(state.dictationBase, parts.join(' '));
            prompt.dispatchEvent(new Event('input'));

            const maxLength = parseInt(prompt.maxLength || '0', 10);
            if (maxLength > 0 && prompt.value.length >= maxLength)
            {
                state.dictationHadError = true;
                setDictationStatus('The maximum request length was reached, so dictation stopped.', true);
                stopDictation();
            }
        };

        recognition.onerror = (event) => {
            if (state.recognition !== recognition)
            {
                return;
            }

            const code = String(event && event.error || '').trim().toLowerCase();
            releaseDictation(recognition);

            if (code !== 'aborted')
            {
                setDictationStatus(dictationErrorMessage(code), true);
            }
        };

        recognition.onend = () => {
            const hadError = state.dictationHadError;

            if (releaseDictation(recognition) && !hadError)
            {
                setDictationStatus('Dictation added. Review your request or continue typing.');
                prompt.focus();
            }
        };

        try
        {
            recognition.start();
            return true;
        }
        catch (error)
        {
            releaseDictation(recognition);
            setDictationStatus(dictationErrorMessage(error && error.name), true);
            return false;
        }
    };

    const csrfToken = () => {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute('content') : '';
    };

    const setOpen = (open) => {
        if (!open)
        {
            stopDictation(true);
        }

        root.hidden = !open;
        document.body.classList.toggle('overflow-hidden', open);

        if (open)
        {
            window.setTimeout(() => prompt.focus(), 30);
        }
    };

    const setBusy = (busy, label) => {
        if (busy && (state.dictating || state.dictationStopping))
        {
            stopDictation(true);
            setDictationStatus('');
        }

        state.busy = busy;
        generateButton.disabled = busy;
        applyButton.disabled = busy;
        discardButton.disabled = busy;
        generateButton.classList.toggle('opacity-70', busy);
        applyButton.classList.toggle('opacity-70', busy);
        discardButton.classList.toggle('opacity-70', busy);
        generateLabel.textContent = label || 'Ask PonoAI';
        updateDictationButton();
    };

    const setStatus = (message, isError = false) => {
        status.textContent = message;
        status.classList.toggle('text-rose-600', isError);
        status.classList.toggle('dark:text-rose-300', isError);
        status.classList.toggle('text-slate-500', !isError);
        status.classList.toggle('dark:text-slate-400', !isError);
    };

    const requestJson = async (url, payload) => {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
        });
        let data = {};

        try
        {
            data = await response.json();
        }
        catch (error)
        {
            data = {};
        }

        if (!response.ok || data.success !== true)
        {
            const validationMessage = data.errors
                ? Object.values(data.errors).flat().find((message) => typeof message === 'string')
                : null;
            let responseMessage = `PonoAI could not complete that request (server status ${response.status}).`;

            if (response.status === 419)
            {
                responseMessage = 'Your admin session expired. Reload this page and try again.';
            }
            else if ([502, 503, 504].includes(response.status))
            {
                responseMessage = `The web server stopped waiting for PonoAI (status ${response.status}). Nothing was changed.`;
            }

            throw new Error(validationMessage || data.message || responseMessage);
        }

        return data;
    };

    document.querySelectorAll('[data-pono-ai-page-editor-open]').forEach((button) => {
        button.addEventListener('click', () => setOpen(true));
    });

    root.querySelectorAll('[data-pono-ai-editor-close]').forEach((button) => {
        button.addEventListener('click', () => {
            if (!state.busy)
            {
                setOpen(false);
            }
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !root.hidden && !state.busy)
        {
            setOpen(false);
        }
    });

    prompt.addEventListener('input', () => {
        characterCount.textContent = `${prompt.value.length} / ${prompt.maxLength}`;
    });

    if (dictationButton)
    {
        dictationButton.addEventListener('click', () => {
            if (state.busy || state.dictationStopping)
            {
                return;
            }

            if (state.dictating)
            {
                stopDictation();
                return;
            }

            startDictation();
        });
    }
    updateDictationButton();

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (state.dictating || state.dictationStopping)
        {
            stopDictation(true);
        }
        setDictationStatus('');

        if (state.busy || !prompt.value.trim())
        {
            if (!prompt.value.trim())
            {
                setStatus('Describe the change you want PonoAI to make.', true);
                prompt.focus();
            }
            return;
        }

        setBusy(true, 'Preparing draft...');
        setStatus('PonoAI is working. Nothing will be changed without your approval.');

        try
        {
            const payload = {
                prompt: prompt.value.trim(),
                entry_section_id: parseInt(root.dataset.entrySectionId || '0') || null,
                draft_uuid: state.draftUuid,
                expected_revision_sha256: state.revisionSha256,
            };
            const data = await requestJson(root.dataset.generateUrl, payload);
            state.draftUuid = data.draft_uuid;
            state.revisionSha256 = data.revision_sha256;
            state.livePageSha256 = data.live_page_sha256;
            const reviewOnly = data.review_only === true;
            const draftHasChanges = data.draft_has_changes === true;
            const previewUrl = typeof data.preview_url === 'string' ? data.preview_url.trim() : '';
            const visualPreview = data.visual_preview && typeof data.visual_preview === 'object'
                ? data.visual_preview
                : null;
            const visualReviewMessage = visualPreview && typeof visualPreview.message === 'string'
                ? visualPreview.message.trim()
                : '';
            resultLabel.textContent = reviewOnly ? 'Review complete' : 'Draft ready';
            summary.textContent = data.summary || 'PonoAI prepared the requested changes.';
            changes.replaceChildren();

            (Array.isArray(data.changes) ? data.changes : []).forEach((change) => {
                const item = document.createElement('li');
                item.className = 'flex items-start gap-2';
                const icon = document.createElement('i');
                icon.className = reviewOnly
                    ? 'fa-solid fa-circle-info mt-1.5 text-[10px] text-blue-600 dark:text-blue-300'
                    : 'fa-solid fa-circle-check mt-1.5 text-[10px] text-emerald-600 dark:text-emerald-300';
                const text = document.createElement('span');
                text.textContent = change;
                item.append(icon, text);
                changes.append(item);
            });

            preview.href = previewUrl || '#';
            preview.hidden = !previewUrl;
            applyButton.hidden = !draftHasChanges;
            discardButton.hidden = !draftHasChanges;
            result.hidden = false;
            prompt.value = '';
            prompt.dispatchEvent(new Event('input'));
            const resultStatus = reviewOnly
                ? 'Review complete. Nothing was changed.'
                : (draftHasChanges
                    ? 'Draft ready. Preview it, request another change, or apply this exact revision.'
                    : 'The current draft already matches that request.');
            setStatus(`${resultStatus}${visualReviewMessage ? ` ${visualReviewMessage}` : ''}`);
        }
        catch (error)
        {
            setStatus(error.message || 'PonoAI could not prepare those changes.', true);
        }
        finally
        {
            setBusy(false);
        }
    });

    preview.addEventListener('click', (event) => {
        event.preventDefault();
        const previewUrl = preview.getAttribute('href');

        if (!previewUrl || previewUrl === '#')
        {
            return;
        }

        window.open(previewUrl, '_blank', 'noopener,noreferrer');
    });

    const applyDraft = async () => {
        if (state.busy || !state.draftUuid || !state.revisionSha256 || !state.livePageSha256)
        {
            return;
        }

        setBusy(true, 'Preparing another change');
        setStatus('Applying the exact approved revision and verifying the live page...');

        try
        {
            const data = await requestJson(root.dataset.applyUrl, {
                draft_uuid: state.draftUuid,
                expected_revision_sha256: state.revisionSha256,
                expected_live_page_sha256: state.livePageSha256,
                confirmation: root.dataset.applyConfirmation,
                entry_section_id: parseInt(root.dataset.entrySectionId || '0') || null,
            });
            const refreshUrl = typeof data.refresh_url === 'string' && data.refresh_url.trim()
                ? data.refresh_url.trim()
                : root.dataset.successUrl;
            setStatus(data.message || 'The approved changes were applied and saved. Refreshing the editor...');
            window.setTimeout(() => window.location.replace(refreshUrl), 700);
        }
        catch (error)
        {
            setStatus(error.message || 'The draft could not be applied. No partial change was saved.', true);
            setBusy(false);
        }
    };

    const discardDraft = async () => {
        if (state.busy || !state.draftUuid || !state.revisionSha256)
        {
            return;
        }

        setBusy(true, 'Preparing another change');
        setStatus('Discarding this isolated draft...');

        try
        {
            const data = await requestJson(root.dataset.discardUrl, {
                draft_uuid: state.draftUuid,
                expected_revision_sha256: state.revisionSha256,
            });
            state.draftUuid = null;
            state.revisionSha256 = null;
            state.livePageSha256 = null;
            preview.href = '#';
            applyButton.hidden = true;
            result.hidden = true;
            summary.textContent = '';
            changes.replaceChildren();
            setBusy(false);
            setStatus(data.message || 'The PonoAI draft was discarded. Nothing changed on the live page.');
            prompt.focus();
        }
        catch (error)
        {
            setStatus(error.message || 'The draft could not be discarded. Nothing changed on the live page.', true);
            setBusy(false);
        }
    };

    const handleConfirmedAction = (event) => {
        const action = event && event.detail ? event.detail.action : null;

        if (action === 'apply')
        {
            applyDraft();
        }
        else if (action === 'discard')
        {
            discardDraft();
        }
    };
    document.addEventListener('ponoai-website-page-action-confirmed', handleConfirmedAction);
    window.addEventListener('pagehide', () => stopDictation(true));

    applyButton.addEventListener('click', () => {
        if (state.busy || !state.draftUuid || !state.revisionSha256 || !state.livePageSha256)
        {
            return;
        }

        setStatus('Waiting for your explicit approval...');
        document.dispatchEvent(new CustomEvent('ponoai-website-page-request-confirm', {
            detail: { action: 'apply' },
        }));
    });

    discardButton.addEventListener('click', () => {
        if (state.busy || !state.draftUuid || !state.revisionSha256)
        {
            return;
        }

        setStatus('Waiting for your confirmation...');
        document.dispatchEvent(new CustomEvent('ponoai-website-page-request-confirm', {
            detail: { action: 'discard' },
        }));
    });

    return true;
};

const bindWebsitePagePonoAiEditorWhenReady = () => {
    if (bindWebsitePagePonoAiEditor())
    {
        return;
    }

    const appRoot = document.getElementById('app') || document.body;
    const observer = new MutationObserver(() => {
        if (bindWebsitePagePonoAiEditor())
        {
            observer.disconnect();
        }
    });
    observer.observe(appRoot, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
};

if (document.readyState === 'loading')
{
    document.addEventListener('DOMContentLoaded', bindWebsitePagePonoAiEditorWhenReady, { once: true });
}
else
{
    bindWebsitePagePonoAiEditorWhenReady();
}
